import { useEffect, useMemo, useRef, useState } from 'react';
import { Select } from 'antd';
import { CheckmarkIcon } from '../../../assets/icons';
import {
  useGetDepartmentCatalogQuery,
  useGetDepartmentsQuery,
  type CatalogDepartment as Department,
} from '../../../features/departments/departmentsApi';
import { useGetStateDepartmentsQuery } from '../../../features/platform/platformApi';
import { usePlatformState } from '../../../features/platform/platformStateSlice';

/* ------------------------------------------------------------------ *
 * Create Organization — Step 3: Select Departments (Figma 1724-2415).
 * Pick the departments to configure a location hierarchy for. A pinned
 * "Party" card represents the organization itself (always selected).
 * The list is the active state's enabled departments
 * (GET /platform/states/<code>/departments/); category is enriched from the
 * global catalog (GET /govt-department/catalog/), which is also the fallback
 * — then a static master list — so the step is never empty.
 * ------------------------------------------------------------------ */

/** Fallback master list if the catalog API returns nothing. */
const FALLBACK_DEPARTMENTS: Department[] = [
  { code: '01', name: 'Revenue & Land Administration', category: 'Administrative' },
  { code: '02', name: 'Home & Internal Security', category: 'Law & Order' },
  { code: '03', name: 'Panchayati Raj & Rural Local Gov.', category: 'Rural Dev' },
  { code: '04', name: 'Urban Local Bodies and UIT', category: 'Urban Dev' },
  { code: '05', name: 'Election Administration', category: 'Governance' },
  { code: '06', name: 'Electricity Distribution (JVVNL)', category: 'Power & Energy' },
  { code: '07', name: 'Public Works Department (PWD)', category: 'Infrastructure' },
  { code: '08', name: 'Water Supply (PHED / JJM)', category: 'Utilities' },
  { code: '09', name: 'Water Resources & Irrigation', category: 'Irrigation' },
  { code: '10', name: 'Forest & Wildlife Administration', category: 'Environment' },
  { code: '11', name: 'School Education', category: 'Education' },
  { code: '12', name: 'Medical, Health & Family Welfare', category: 'Healthcare' },
  { code: '13', name: 'Women & Child Development', category: 'Welfare' },
  { code: '14', name: 'Agriculture Extension', category: 'Agriculture' },
  { code: '15', name: 'Animal Husbandry', category: 'Veterinary' },
  { code: '16', name: 'Cooperative Department', category: 'Cooperation' },
  { code: '17', name: 'Food & Civil Supplies (PDS)', category: 'Civil Supplies' },
  { code: '18', name: 'Commercial Taxes (GST)', category: 'Revenue' },
  { code: '19', name: 'State Excise', category: 'Excise' },
  { code: '20', name: 'Treasury & Accounts', category: 'Finance' },
  { code: '21', name: 'Mines & Geology', category: 'Mining' },
  { code: '22', name: 'Transport & Road Safety', category: 'Transport' },
  { code: '23', name: 'Courts & Judiciary', category: 'Judicial' },
  { code: '24', name: 'Information Technology & Comm.', category: 'Digital/IT' },
  { code: '25', name: 'Disaster Management & Relief', category: 'Emergency' },
];

/** One selectable department card. */
function DeptCard({
  dept,
  selected,
  onToggle,
}: {
  dept: Department;
  selected: boolean;
  onToggle?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={[
        'flex cursor-pointer items-start gap-3 rounded-lg border p-4 text-left transition-colors',
        selected ? 'border-brand bg-[#eff6ff]' : 'border-line-soft bg-white hover:border-[#bfdbfe]',
      ].join(' ')}
    >
      <span
        className={[
          'mt-0.5 flex size-[18px] shrink-0 items-center justify-center rounded border transition-colors',
          selected ? 'border-brand bg-brand text-white' : 'border-[#cbd5e1] bg-white',
        ].join(' ')}
      >
        {selected && <CheckmarkIcon className="size-3" />}
      </span>
      <div className="flex min-w-0 flex-col gap-1.5">
        <span
          className={[
            'text-[11px] font-bold uppercase tracking-[0.5px]',
            selected ? 'text-brand' : 'text-[#94a3b8]',
          ].join(' ')}
        >
          CODE {dept.code}
        </span>
        <span className="text-sm font-bold leading-5 text-ink">{dept.name}</span>
        <span className="inline-flex w-fit rounded bg-[#f1f5f9] px-2 py-0.5 text-[11px] font-medium text-[#64748b]">
          {dept.category}
        </span>
      </div>
    </button>
  );
}

export default function SelectDepartmentsStep({
  onChange,
  organization,
}: {
  /** Reports the selected department codes to the wizard. */
  onChange?: (codes: string[]) => void;
  /** In edit mode, the org whose already-selected departments should be prefilled. */
  organization?: number;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [category, setCategory] = useState<string>('All Categories');

  // The active state's enabled departments drive the list; the global catalog supplies
  // each department's category (state departments carry none) and is the first fallback.
  const { code: stateCode } = usePlatformState();
  const { data: stateDepts } = useGetStateDepartmentsQuery(stateCode, { skip: !stateCode });
  const { data: catalog } = useGetDepartmentCatalogQuery();

  const categoryByCode = useMemo(() => {
    const m = new Map<string, string>();
    (catalog ?? []).forEach((d) => m.set(d.code, d.category));
    return m;
  }, [catalog]);

  const departments: Department[] = useMemo(() => {
    if (stateDepts?.length) {
      return stateDepts.map((d) => ({
        code: d.code,
        name: d.name,
        category: categoryByCode.get(d.code) || d.control || 'Uncategorized',
      }));
    }
    return catalog?.length ? catalog : FALLBACK_DEPARTMENTS;
  }, [stateDepts, catalog, categoryByCode]);

  // Edit mode — the org's saved departments (prefill the checkboxes once they load).
  const { data: orgDepts } = useGetDepartmentsQuery(
    { organization: organization as number, pageSize: 100 },
    { skip: organization == null },
  );
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || organization == null || !orgDepts) return;
    const byCode = new Map(departments.map((d) => [d.code, d]));
    const byName = new Map(departments.map((d) => [d.name.trim().toLowerCase(), d]));
    const codes = new Set<string>();
    for (const d of orgDepts.items) {
      // Match a saved department to a catalog entry by its short code, then by name.
      const match =
        (d.short_code && byCode.get(d.short_code)) ||
        byName.get((d.name ?? '').trim().toLowerCase());
      if (match) codes.add(match.code);
    }
    setSelected(codes);
    prefilled.current = true;
  }, [orgDepts, organization, departments]);

  useEffect(() => {
    onChange?.(Array.from(selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const categories = useMemo(
    () => ['All Categories', ...Array.from(new Set(departments.map((d) => d.category))).sort()],
    [departments],
  );
  const visible = useMemo(
    () =>
      category === 'All Categories'
        ? departments
        : departments.filter((d) => d.category === category),
    [departments, category],
  );

  const toggle = (code: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  const selectAll = () => setSelected(new Set(departments.map((d) => d.code)));
  const deselectAll = () => setSelected(new Set());

  return (
    <div className="flex flex-col gap-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line-soft bg-white p-3 shadow-card">
        <div className="flex flex-wrap items-center gap-4">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-shade px-3 py-1 text-xs font-semibold text-brand">
            <span className="size-1.5 rounded-full bg-brand" />
            {selected.size} of {departments.length} Selected
          </span>
          <button
            type="button"
            onClick={selectAll}
            className="text-xs font-semibold text-brand hover:underline"
          >
            Select All ({departments.length})
          </button>
          <button
            type="button"
            onClick={deselectAll}
            className="text-xs font-medium text-[#64748b] hover:text-ink"
          >
            Deselect All
          </button>
        </div>
        <Select
          className="min-w-[180px]"
          value={category}
          onChange={setCategory}
          options={categories.map((c) => ({ value: c, label: c }))}
          popupMatchSelectWidth={false}
        />
      </div>

      {/* Department grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((dept) => (
          <DeptCard
            key={dept.code}
            dept={dept}
            selected={selected.has(dept.code)}
            onToggle={() => toggle(dept.code)}
          />
        ))}
      </div>
    </div>
  );
}
